<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.groups', 2),
                        href: meta.links.group_index,
                    },
                    {
                        name: group.name,
                        href: meta.links.group_show,
                    },
                    {
                        name: $t('messages.perseverance'),
                        href: null,
                    },
                ]"
            >
                <template v-slot:corner>
                    <div>
                        <ActivityMenuOptions>
                            <InertiaLink
                                v-for="otherGroup in meta.links.otherGroups"
                                :href="otherGroup.url"
                                :key="otherGroup.url"
                                class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                role="menuitem"
                            >
                                {{ otherGroup.text }}
                            </InertiaLink>
                        </ActivityMenuOptions>
                    </div>
                </template>
            </SmartBreadcrumbs>
        </template>

        <EmptyState v-if="subjects.length === 0" picture />
        <div v-else class="flex flex-wrap gap-x-2">
            <AppButton
                element="InertiaLink"
                :href="subject.url"
                v-for="subject in subjects"
                :key="subject.id"
                class="flex gap-x-2"
            >
                {{ subject.text }}

                <IconCircleCheckFilled class="h-5 w-5 text-indigo-600" v-if="filters.subject_id == subject.id" />
            </AppButton>
        </div>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter" v-if="filters.subject_id">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.tags', 1)" for="tag_id" />
                        <FormSelect
                            id="tag_id"
                            v-model="form.tag_id"
                            :options="options.tags"
                            class="mt-1 w-full"
                            optgroups
                            required
                        />
                        <FormError :value="form.errors.tag_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="">
                        <FormLabel :value="$t('messages.difficulty')" for="difficulty_in" />
                        <SlideOverCheckboxInput v-model="form.difficulty_in" :options="options.difficulties" />
                        <FormError :value="form.errors.difficulty_in" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.activity_mode')" for="mode" />
                        <FormSelect
                            id="mode"
                            v-model="form.mode"
                            :options="options.modes"
                            class="mt-1 w-full"
                            :null-option-text="$t('messages.all')"
                        />
                        <FormError :value="form.errors.mode" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.date_from')" for="date_from">
                            <ContextMenu>
                                <ul>
                                    <li v-for="period in options.date_periods" :key="period.url" class="mt-1">
                                        <AppButton element="InertiaLink" class="block" :href="period.url" size="sm">
                                            {{ period.text }}
                                        </AppButton>
                                    </li>
                                </ul>
                            </ContextMenu>
                        </FormLabel>
                        <FormInput
                            id="date_from"
                            v-model="form.date_from"
                            type="date"
                            name="date_from"
                            class="mt-1 w-full"
                            required
                        />
                        <FormError :value="form.errors.date_from" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.date_to')" for="date_to" />
                        <FormInput
                            id="date_to"
                            v-model="form.date_to"
                            type="date"
                            name="date_to"
                            class="mt-1 w-full"
                            required
                        />
                        <FormError :value="form.errors.date_to" class="mt-2" />
                    </div>
                </div>

                <div class="flex justify-end mt-5">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div class="mt-5" v-if="perseverance !== null">
            <ApexGraph :options="perseverance.boxplot" />

            <div class="mt-5">
                <div v-for="student in perseverance.students" :key="student.id" class="mt-3">
                    <div>
                        <StudentInfoItem :student="student" />
                    </div>
                    <div class="mt-1">
                        <ApexGraph :options="perseverance.graph" :series="student.series" />
                    </div>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import AppButton from '@/components/AppButton.vue';
import ActivityMenuOptions from '@/components/pageSpecific/ActivityMenuOptions.vue';
import { IconCircleCheckFilled } from '@tabler/icons-vue';
import EmptyState from '@/components/EmptyState.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import ContextMenu from '@/components/ContextMenu.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import ApexGraph from '../../../../../../components/ApexGraph.vue';
import StudentInfoItem from '../../../../../../components/StudentInfoItem.vue';
import SlideOverCheckboxInput from '../../../../../../components/Forms/SlideOverCheckboxInput.vue';

export default {
    components: {
        SlideOverCheckboxInput,
        StudentInfoItem,
        ApexGraph,
        SubmitFormButton,
        ContextMenu,
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
        EmptyState,
        IconCircleCheckFilled,
        ActivityMenuOptions,
        PanelPage,
        AppButton,
    },
    layout: Layout,
    name: 'Index',
    props: ['subjects', 'perseverance', 'group', 'filters', 'options', 'meta'],
    data() {
        return {
            form: this.$inertia.form(this.filters),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index, {
                preserveScroll: true,
            });
        },
    },
};
</script>
